import React, { useState, useRef, useEffect } from 'react';
import { refineTestCases } from '../services/api';

/**
 * RefineChatPanel — A right-side slide-out panel providing a conversational
 * interface to refine generated test cases. Includes version history so users
 * can revert to any prior generation if they don't like the current changes.
 */
function RefineChatPanel({ isOpen, onClose, testCases, onUpdate, userStory, onUserStoryChange }) {
  const [input, setInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [refining, setRefining] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [versions, setVersions] = useState([]); // { label, testCases, timestamp }
  const [showVersions, setShowVersions] = useState(false);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [panelWidth, setPanelWidth] = useState(400);
  const [showUserStory, setShowUserStory] = useState(false);
  const [editingStory, setEditingStory] = useState(false);
  const [storyDraft, setStoryDraft] = useState('');
  const timerRef = useRef(null);
  const abortRef = useRef(null);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const resizingRef = useRef(false);

  // Toggle body class to collapse sidebar when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('refine-panel-active');
      document.documentElement.style.setProperty('--refine-panel-width', `${panelWidth}px`);
    } else {
      document.body.classList.remove('refine-panel-active');
      document.documentElement.style.removeProperty('--refine-panel-width');
    }
    return () => {
      document.body.classList.remove('refine-panel-active');
      document.documentElement.style.removeProperty('--refine-panel-width');
    };
  }, [isOpen, panelWidth]);

  // Resize drag handlers
  const handleResizeStart = (e) => {
    e.preventDefault();
    resizingRef.current = true;
    const startX = e.clientX;
    const startWidth = panelWidth;

    const handleMouseMove = (moveEvent) => {
      if (!resizingRef.current) return;
      const delta = startX - moveEvent.clientX;
      const newWidth = Math.min(400, Math.max(280, startWidth + delta));
      setPanelWidth(newWidth);
      document.documentElement.style.setProperty('--refine-panel-width', `${newWidth}px`);
    };

    const handleMouseUp = () => {
      resizingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.classList.remove('refine-resizing');
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.body.classList.add('refine-resizing');
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Initialize version history with the first set of test cases
  useEffect(() => {
    if (isOpen && testCases.length > 0 && versions.length === 0) {
      setVersions([{
        label: 'Original generation',
        testCases: [...testCases],
        timestamp: new Date().toISOString()
      }]);
    }
    // eslint-disable-next-line
  }, [isOpen, testCases.length]);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => clearInterval(timerRef.current);
  }, []);

  const handleSend = async () => {
    if (!input.trim() || testCases.length === 0 || refining) return;
    const instruction = input.trim();
    setInput('');
    setHistoryIndex(-1);

    const newHistory = [...chatHistory, { role: 'user', text: instruction }];
    setChatHistory(newHistory);
    setRefining(true);
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
    const abortController = new AbortController();
    abortRef.current = abortController;
    const startTime = Date.now();

    try {
      const result = await refineTestCases(testCases, instruction, chatHistory, abortController.signal, userStory);
      const elapsedSec = ((Date.now() - startTime) / 1000).toFixed(1);
      const refined = result.testCases || [];
      const reply = result.reply || 'Done.';

      // If test cases actually changed, save the version
      const casesChanged = JSON.stringify(refined) !== JSON.stringify(testCases);

      if (refined.length > 0 && casesChanged) {
        // Save current state as a version before applying changes
        setVersions(prev => [...prev, {
          label: instruction.length > 50 ? instruction.substring(0, 50) + '...' : instruction,
          testCases: [...refined],
          timestamp: new Date().toISOString()
        }]);
        onUpdate(refined);
        setChatHistory(prev => [...prev, {
          role: 'assistant',
          text: reply,
          elapsed: elapsedSec,
          hasChanges: true
        }]);
      } else {
        // Read-only response or no changes — just show the reply
        setChatHistory(prev => [...prev, {
          role: 'assistant',
          text: reply,
          elapsed: elapsedSec,
          hasChanges: false
        }]);
      }
    } catch (err) {
      if (err.name === 'AbortError' || abortController.signal.aborted) {
        setChatHistory(prev => [...prev, { role: 'system', text: 'Cancelled.' }]);
      } else {
        setChatHistory(prev => [...prev, {
          role: 'system',
          text: `Something went wrong: ${err.message || 'Unknown error'}`
        }]);
      }
    } finally {
      clearInterval(timerRef.current);
      abortRef.current = null;
      setRefining(false);
    }
  };

  const handleStop = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const handleRevert = (versionIndex) => {
    const version = versions[versionIndex];
    if (!version) return;
    onUpdate([...version.testCases]);
    setChatHistory(prev => [...prev, {
      role: 'system',
      text: `⏪ Reverted to: "${version.label}" (${version.testCases.length} test cases)`
    }]);
    // Remove versions after the reverted one
    setVersions(prev => prev.slice(0, versionIndex + 1));
    setShowVersions(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !refining) {
      e.preventDefault();
      handleSend();
    } else if (e.key === 'ArrowUp') {
      const userMessages = chatHistory.filter(m => m.role === 'user');
      if (userMessages.length === 0) return;
      e.preventDefault();
      const newIndex = historyIndex === -1 ? userMessages.length - 1 : Math.max(0, historyIndex - 1);
      setHistoryIndex(newIndex);
      setInput(userMessages[newIndex].text);
    } else if (e.key === 'ArrowDown') {
      const userMessages = chatHistory.filter(m => m.role === 'user');
      if (historyIndex === -1) return;
      e.preventDefault();
      const newIndex = historyIndex + 1;
      if (newIndex >= userMessages.length) {
        setHistoryIndex(-1);
        setInput('');
      } else {
        setHistoryIndex(newIndex);
        setInput(userMessages[newIndex].text);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="refine-panel" style={{ width: panelWidth }}>
      {/* Resize handle */}
      <div className="refine-panel-resize-handle" onMouseDown={handleResizeStart} />
      {/* Header */}
      <div className="refine-panel-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '1.1rem' }}>✨</span>
            <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600 }}>Refine with AI</h3>
            <span className="badge badge-info" style={{ fontSize: '0.7rem' }}>
              {testCases.length} test cases
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowVersions(!showVersions)}
              title="Version history"
              style={{ fontSize: '0.78rem', padding: '4px 8px' }}
            >
              🕐 {versions.length > 1 ? `v${versions.length}` : 'History'}
            </button>
            <button
              className="refine-panel-close"
              onClick={onClose}
              aria-label="Close refine panel"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Version History Dropdown */}
        {showVersions && versions.length > 0 && (
          <div className="refine-versions">
            <div style={{ fontSize: '0.78rem', fontWeight: 600, padding: '8px 12px', borderBottom: '1px solid var(--border, #e0e0e0)', color: 'var(--text-secondary)' }}>
              Version History — click to revert
            </div>
            {versions.map((v, i) => (
              <div
                key={i}
                className={`refine-version-item ${i === versions.length - 1 ? 'current' : ''}`}
                onClick={() => i < versions.length - 1 ? handleRevert(i) : null}
                style={{ cursor: i < versions.length - 1 ? 'pointer' : 'default' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>
                    {i === 0 ? '🏁' : '✏️'} v{i + 1}: {v.label}
                  </span>
                  {i === versions.length - 1 && (
                    <span className="badge badge-success" style={{ fontSize: '0.65rem' }}>current</span>
                  )}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                  {v.testCases.length} test cases · {new Date(v.timestamp).toLocaleTimeString('en-US', { timeZone: 'America/Chicago' })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* User Story Reference (pinned above scrollable chat) */}
        <div className="refine-user-story-ref">
          <button
            className="refine-user-story-toggle"
            onClick={() => setShowUserStory(!showUserStory)}
          >
            <span>📋 User Story Reference</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {userStory ? (
                <span className="badge badge-success" style={{ fontSize: '0.63rem' }}>Set</span>
              ) : (
                <span className="badge badge-warning" style={{ fontSize: '0.63rem' }}>Not set</span>
              )}
              <span style={{ fontSize: '0.75rem' }}>{showUserStory ? '▾' : '▸'}</span>
            </span>
          </button>
          {showUserStory && (
            <div className="refine-user-story-content">
              {editingStory ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <textarea
                    className="input"
                    rows={5}
                    value={storyDraft}
                    onChange={e => setStoryDraft(e.target.value)}
                    placeholder={"Paste your user story or requirements here...\n\nAs a [user], I want to [action], so that [benefit].\n\nAcceptance Criteria:\n- ..."}
                    style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: '0.78rem' }}
                    autoFocus
                  />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-primary btn-sm" style={{ fontSize: '0.72rem' }} onClick={() => {
                      if (onUserStoryChange) onUserStoryChange(storyDraft);
                      setEditingStory(false);
                    }}>Save</button>
                    <button className="btn btn-secondary btn-sm" style={{ fontSize: '0.72rem' }} onClick={() => setEditingStory(false)}>Cancel</button>
                  </div>
                </div>
              ) : userStory ? (
                <div>
                  <div style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{userStory}</div>
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ marginTop: 8, fontSize: '0.72rem' }}
                    onClick={() => { setStoryDraft(userStory); setEditingStory(true); }}
                  >✏️ Edit</button>
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '0 0 8px' }}>
                    No user story attached. Add one to give AI better context for refinements.
                  </p>
                  <button
                    className="btn btn-primary btn-sm"
                    style={{ fontSize: '0.72rem' }}
                    onClick={() => { setStoryDraft(''); setEditingStory(true); }}
                  >➕ Add User Story</button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Chat Messages */}
        <div className="refine-chat-messages">
          {chatHistory.length === 0 && (
            <div className="refine-empty-state">
              <div style={{ fontSize: '2rem', marginBottom: 8 }}>💬</div>
              <p style={{ fontWeight: 500, margin: '0 0 8px' }}>Chat with AI to refine your test cases</p>
              <div className="refine-suggestions">
                <button onClick={() => setInput('Add edge cases for error handling')}>
                  Add edge cases for error handling
                </button>
                <button onClick={() => setInput('Change all priorities to P1-High')}>
                  Change all priorities to P1-High
                </button>
                <button onClick={() => setInput('How many test cases are negative type?')}>
                  How many test cases are negative type?
                </button>
                <button onClick={() => setInput('Remove the Locale column')}>
                  Remove the Locale column
                </button>
              </div>
            </div>
          )}

          {chatHistory.map((msg, i) => (
            <div key={i} className={`refine-msg refine-msg-${msg.role}`}>
              {msg.role === 'user' && (
                <div className="refine-msg-bubble refine-msg-user-bubble">
                  {msg.text}
                </div>
              )}
              {msg.role === 'assistant' && (
                <div className="refine-msg-bubble refine-msg-ai-bubble">
                  <div className="refine-msg-ai-text">{msg.text}</div>
                  {msg.hasChanges && (
                    <div className="refine-msg-badge">
                      <span className="badge badge-success" style={{ fontSize: '0.68rem' }}>✓ Test cases updated</span>
                    </div>
                  )}
                  {msg.elapsed && (
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                      {msg.elapsed}s
                    </div>
                  )}
                </div>
              )}
              {msg.role === 'system' && (
                <div className="refine-msg-system">
                  {msg.text}
                </div>
              )}
            </div>
          ))}

          {refining && (
            <div className="refine-msg refine-msg-assistant">
              <div className="refine-msg-bubble refine-msg-ai-bubble refine-thinking">
                <span className="refine-dots">
                  <span></span><span></span><span></span>
                </span>
                Thinking... {elapsed}s
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Input Area */}
        <div className="refine-input-area">
          <div className="refine-input-wrapper">
            <textarea
              ref={inputRef}
              className="refine-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything about your test cases..."
              disabled={refining}
              rows={1}
              style={{ minHeight: 40, maxHeight: 100 }}
            />
            {refining ? (
              <button
                className="refine-send-btn refine-stop-btn"
                onClick={handleStop}
                title="Stop"
              >
                ⏹
              </button>
            ) : (
              <button
                className="refine-send-btn"
                onClick={handleSend}
                disabled={!input.trim()}
                title="Send (Enter)"
              >
                ↑
              </button>
            )}
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', padding: '4px 8px 0', textAlign: 'center' }}>
            Press Enter to send · AI focuses on improving your test cases
          </div>
        </div>
      </div>
  );
}

export default RefineChatPanel;
